import { DEFAULT_TOKEN_SCOPES, TOKEN_SCOPES } from '@accounting/shared';
import type { TokenScope } from '@accounting/shared';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Checkbox,
  Code,
  CopyButton,
  Group,
  Image,
  Loader,
  Modal,
  PasswordInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useState } from 'react';
import type { ReactNode } from 'react';
import QRCode from 'qrcode';
import { z } from 'zod';
import {
  beginTwoFactor,
  changePassword,
  confirmTwoFactor,
  disableTwoFactor,
  getSessionUser,
} from '../api/account.js';
import type { TotpEnrolment } from '../api/account.js';
import {
  useAccessTokens,
  useCreateAccessToken,
  useProfile,
  useRevokeAccessToken,
  useUpdateProfile,
} from '../api/hooks.js';
import type { AccessToken } from '../api/hooks.js';
import { ErrorMessage, describeError } from '../components/ErrorMessage.js';
import { PushSettings } from '../components/PushSettings.js';
import { translating } from '../i18n/resolver.js';
import { LOCALES } from '../i18n/core.js';
import type { Locale } from '../i18n/core.js';
import type { MessageKey } from '../i18n/da.js';
import { useI18n, useT } from '../i18n/useT.js';
import { formatDateTime } from '../format.js';

/**
 * Min konto — the person, not the company.
 *
 * It sits in the header cluster rather than under Indstillinger, and that
 * placement is the point rather than a detail. Nothing on this page changes
 * when the company switcher moves: one name, one password, one second factor,
 * one access token. Filing it under a heading whose first entry is named after
 * the active company would imply the opposite — most damagingly for the access
 * token, which ADR-007 makes deliberately per-user.
 *
 * Four cards, in the order a person needs them: who you are, how you sign in,
 * the second factor, and the key your AI holds.
 */

// -----------------------------------------------------------------------------
// Profile
// -----------------------------------------------------------------------------

const profileSchema = z.object({
  displayName: z.string().trim().max(255),
});

function ProfileCard({ email }: { email: string | null }): ReactNode {
  const t = useT();
  const profile = useProfile();
  const save = useUpdateProfile();
  const [loaded, setLoaded] = useState(false);

  const form = useForm<z.infer<typeof profileSchema>>({
    mode: 'uncontrolled',
    initialValues: { displayName: '' },
    validate: zod4Resolver(profileSchema),
  });

  if (profile.data !== undefined && !loaded) {
    setLoaded(true);
    form.setValues({ displayName: profile.data.displayName ?? '' });
  }

  return (
    <Card withBorder padding="md">
      <Stack gap="md">
        <Text fw={500}>{t('account.you')}</Text>

        {profile.isPending && (
          <Center p="md">
            <Loader size="sm" />
          </Center>
        )}

        {profile.isError && (
          <ErrorMessage error={profile.error} title={t('account.loadFailed')} />
        )}

        {profile.data !== undefined && (
          <form
            noValidate
            onSubmit={form.onSubmit((values) => {
              save.mutate(
                {
                  displayName:
                    values.displayName.trim() === '' ? null : values.displayName.trim(),
                },
                {
                  onSuccess: () => {
                    notifications.show({
                      color: 'green',
                      message: t('account.nameSaved'),
                    });
                  },
                },
              );
            })}
          >
            <Stack gap="md">
              <TextInput
                label={t('account.name')}
                description={t('account.nameHelp')}
                key={form.key('displayName')}
                {...form.getInputProps('displayName')}
              />

              {/*
                Read-only, and from the session rather than from /v1/users/me:
                Better Auth owns credentials, and our `users` table has no email
                column. Changing it needs a verification flow that does not
                exist yet, so the field says so instead of offering a save
                button that cannot work.
              */}
              <TextInput
                label={t('account.email')}
                value={email ?? ''}
                disabled
                readOnly
                description={t('account.emailHelp')}
              />

              {profile.data.lastLoginAt !== null && (
                <Text size="sm" c="dimmed">
                  {t('account.lastLogin', {
                    when: formatDateTime(profile.data.lastLoginAt),
                  })}
                </Text>
              )}

              {save.isError && (
                <ErrorMessage error={save.error} title={t('account.nameFailed')} />
              )}

              <Group>
                <Button type="submit" loading={save.isPending}>
                  Gem
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Stack>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Language
// -----------------------------------------------------------------------------

const LOCALE_LABELS: Record<Locale, MessageKey> = {
  da: 'account.language.da',
  en: 'account.language.en',
};

/**
 * Which language the application is read in.
 *
 * On this page and not under Indstillinger, because it belongs to the reader
 * rather than to the books: two people looking at the same company may want
 * different languages, and switching company does not change what language you
 * read.
 *
 * ## Two writes, and the order matters
 *
 * The switch takes effect **immediately and locally**, then persists. The local
 * change is what a person is asking for when they click; the write to
 * `users.locale` is what makes it follow them to another browser. If the write
 * fails they are told, and the screen stays in the language they chose --
 * reverting it would be a worse answer than a failed save, because the thing
 * they wanted did happen here.
 *
 * ## The honest limit
 *
 * The card says what the language does not cover. The chart of accounts, the
 * VAT codes, SAF-T and OIOUBL are Danish in both locales -- they are
 * Erhvervsstyrelsen's and SKAT's own words, and translating them would be wrong
 * rather than merely expensive. Saying so here is cheaper than letting someone
 * switch to English and conclude the feature is half-built.
 */
function LanguageCard(): ReactNode {
  const { t, locale, setLocale } = useI18n();
  const save = useUpdateProfile();

  function choose(next: string | null): void {
    if (next === null || !LOCALES.includes(next as Locale) || next === locale) {
      return;
    }
    const chosen = next as Locale;
    setLocale(chosen);
    save.mutate(
      { locale: chosen },
      {
        onSuccess: () => {
          notifications.show({ color: 'green', message: t('account.languageSaved') });
        },
      },
    );
  }

  return (
    <Card withBorder padding="md">
      <Stack gap="md">
        <Text fw={500}>{t('account.language')}</Text>

        {/* No label on the Select: the card heading above already is one, and
            two identical labels read as a mistake to a screen reader. */}
        <Select
          aria-label={t('account.languageSelect')}
          description={t('account.languageHelp')}
          data={LOCALES.map((value) => ({ value, label: t(LOCALE_LABELS[value]) }))}
          value={locale}
          onChange={choose}
          allowDeselect={false}
          maw={260}
        />

        <Alert color="gray" variant="light" title={t('account.languageScopeTitle')}>
          <Text size="sm">{t('account.languageScope')}</Text>
        </Alert>

        {save.isError && (
          <ErrorMessage error={save.error} title={t('account.languageFailed')} />
        )}
      </Stack>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Password
// -----------------------------------------------------------------------------

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'account.currentPasswordRequired'),
    newPassword: z.string().min(8, 'account.newPasswordTooShort'),
    repeatPassword: z.string(),
  })
  .refine((values) => values.newPassword === values.repeatPassword, {
    message: 'account.passwordsDiffer',
    path: ['repeatPassword'],
  });

function PasswordCard(): ReactNode {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const form = useForm<z.infer<typeof passwordSchema>>({
    mode: 'uncontrolled',
    initialValues: { currentPassword: '', newPassword: '', repeatPassword: '' },
    validate: translating(passwordSchema, t, 'account'),
  });

  async function submit(values: z.infer<typeof passwordSchema>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await changePassword(values.currentPassword, values.newPassword);
      form.reset();
      notifications.show({
        color: 'green',
        title: t('account.passwordChanged'),
        message: t('account.passwordChangedDetail'),
      });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card withBorder padding="md">
      <Stack gap="md">
        <Text fw={500}>{t('account.password')}</Text>

        <form
          noValidate
          onSubmit={form.onSubmit((values) => {
            void submit(values);
          })}
        >
          <Stack gap="md">
            <PasswordInput
              label={t('account.currentPassword')}
              withAsterisk
              autoComplete="current-password"
              key={form.key('currentPassword')}
              {...form.getInputProps('currentPassword')}
            />
            <PasswordInput
              label={t('account.newPassword')}
              withAsterisk
              autoComplete="new-password"
              key={form.key('newPassword')}
              {...form.getInputProps('newPassword')}
            />
            <PasswordInput
              label={t('account.repeatPassword')}
              withAsterisk
              autoComplete="new-password"
              key={form.key('repeatPassword')}
              {...form.getInputProps('repeatPassword')}
            />

            <Text size="sm" c="dimmed">
              {t('account.passwordHelp')}
            </Text>

            {error !== null && (
              <ErrorMessage error={error} title={t('account.passwordFailed')} />
            )}

            <Group>
              <Button type="submit" loading={busy}>
                {t('account.changePassword')}
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Two-factor
// -----------------------------------------------------------------------------

/**
 * Enrolment, in the two steps Better Auth actually requires.
 *
 * `two-factor/enable` hands back the secret and the backup codes but does
 * **not** switch 2FA on; `two-factor/verify-totp` does. That order is the whole
 * safety of the flow — it proves the authenticator received the secret before
 * the account starts demanding codes from it. A one-step version locks people
 * out of their own books, and this ledger cannot be restored by deleting rows.
 */
function TwoFactorCard({
  enabled,
  onChanged,
}: {
  enabled: boolean;
  onChanged: () => void;
}): ReactNode {
  const t = useT();
  const [opened, setOpened] = useState(false);
  const [enrolment, setEnrolment] = useState<TotpEnrolment | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  function reset(): void {
    setOpened(false);
    setEnrolment(null);
    setQr(null);
    setPassword('');
    setCode('');
    setError(null);
  }

  async function begin(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const started = await beginTwoFactor(password);
      setEnrolment(started);
      /*
        SVG, not `toDataURL`.

        `toDataURL` renders through a canvas, which means it needs a real one:
        jsdom has no canvas implementation, so every test touching this path
        would fail, and any browser with canvas readback restricted would show
        an empty box. `toString({ type: 'svg' })` is pure string work -- it
        stays sharp at any size, and the result goes into an <img> as a data
        URI without `dangerouslySetInnerHTML`.
      */
      const svg = await QRCode.toString(started.totpURI, {
        type: 'svg',
        margin: 1,
        width: 220,
      });
      setQr(`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`);
      setPassword('');
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function confirm(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await confirmTwoFactor(code.trim());
      notifications.show({
        color: 'green',
        title: t('account.twoFactorEnabled'),
        message: t('account.twoFactorEnabledDetail'),
      });
      reset();
      onChanged();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function turnOff(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await disableTwoFactor(password);
      notifications.show({ color: 'green', message: t('account.twoFactorDisabled') });
      reset();
      onChanged();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card withBorder padding="md">
        <Stack gap="md">
          <Group justify="space-between">
            <Text fw={500}>{t('account.twoFactor')}</Text>
            <Badge color={enabled ? 'green' : 'gray'} variant="light">
              {enabled ? t('account.twoFactorOn') : t('account.twoFactorOff')}
            </Badge>
          </Group>

          <Text size="sm" c="dimmed">
            {t('account.twoFactorHelp')}
          </Text>

          <Group>
            <Button
              variant={enabled ? 'default' : 'filled'}
              onClick={() => {
                setOpened(true);
              }}
            >
              {enabled ? t('account.turnOff') : t('account.turnOn')}
            </Button>
          </Group>
        </Stack>
      </Card>

      <Modal
        opened={opened}
        onClose={reset}
        title={enabled ? t('account.turnOffTitle') : t('account.turnOnTitle')}
        size="md"
      >
        <Stack gap="md">
          {enabled ? (
            <>
              <Text size="sm">
                {t('account.turnOffPrompt')}
              </Text>
              <PasswordInput
                label={t('account.password')}
                autoComplete="current-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.currentTarget.value);
                }}
              />
              {error !== null && (
                <Alert color="red" variant="light">
                  {describeError(error, t)}
                </Alert>
              )}
              <Group justify="flex-end">
                <Button variant="default" onClick={reset}>
                  Annullér
                </Button>
                <Button
                  color="red"
                  loading={busy}
                  onClick={() => {
                    void turnOff();
                  }}
                >
                  {t('account.turnOff')}
                </Button>
              </Group>
            </>
          ) : enrolment === null ? (
            <>
              <Text size="sm">
                {t('account.turnOnPrompt')}
              </Text>
              <PasswordInput
                label={t('account.password')}
                autoComplete="current-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.currentTarget.value);
                }}
              />
              {error !== null && (
                <Alert color="red" variant="light">
                  {describeError(error, t)}
                </Alert>
              )}
              <Group justify="flex-end">
                <Button variant="default" onClick={reset}>
                  Annullér
                </Button>
                <Button
                  loading={busy}
                  onClick={() => {
                    void begin();
                  }}
                >
                  {t('account.continue')}
                </Button>
              </Group>
            </>
          ) : (
            <>
              <Text size="sm">
                {t('account.scanPrompt')}
              </Text>

              {qr !== null && (
                <Center>
                  <Image src={qr} alt={t('account.qrAlt')} w={220} h={220} />
                </Center>
              )}

              <Stack gap={4}>
                <Text size="sm" fw={500}>
                  {t('account.cannotScan')}
                </Text>
                <Code block style={{ wordBreak: 'break-all' }}>
                  {enrolment.totpURI}
                </Code>
              </Stack>

              <Alert color="yellow" variant="light" title={t('account.backupCodes')}>
                <Stack gap="xs">
                  <Text size="sm">
                    {t('account.backupCodesHelp')}
                  </Text>
                  <Code block>{enrolment.backupCodes.join('\n')}</Code>
                  <CopyButton value={enrolment.backupCodes.join('\n')}>
                    {({ copied, copy }) => (
                      <Button size="xs" variant="default" onClick={copy}>
                        {copied ? t('common.copied') : t('account.copyBackupCodes')}
                      </Button>
                    )}
                  </CopyButton>
                </Stack>
              </Alert>

              <TextInput
                label={t('account.codeFromApp')}
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(event) => {
                  setCode(event.currentTarget.value);
                }}
              />

              {error !== null && (
                <Alert color="red" variant="light">
                  {describeError(error, t)}
                </Alert>
              )}

              {/*
                Nothing is on yet. Better Auth turns 2FA on in verify-totp, not
                in enable, so closing here leaves the account exactly as it was.
              */}
              <Text size="xs" c="dimmed">
                {t('account.notOnYet')}
              </Text>

              <Group justify="flex-end">
                <Button variant="default" onClick={reset}>
                  Annullér
                </Button>
                <Button
                  loading={busy}
                  disabled={code.trim().length === 0}
                  onClick={() => {
                    void confirm();
                  }}
                >
                  {t('account.turnOn')}
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </>
  );
}

// -----------------------------------------------------------------------------
// Personal access tokens
// -----------------------------------------------------------------------------

const tokenSchema = z.object({
  name: z.string().trim().min(1, 'account.tokenNameRequired').max(100),
});

function statusOf(token: AccessToken): { label: string; color: string } {
  if (token.revokedAt !== null) {
    return { label: 'tilbagekaldt', color: 'red' };
  }
  if (token.expiresAt !== null && new Date(token.expiresAt).getTime() < Date.now()) {
    return { label: 'udløbet', color: 'gray' };
  }
  return { label: 'aktiv', color: 'green' };
}

function AccessTokenCard(): ReactNode {
  const t = useT();
  const tokens = useAccessTokens();
  const create = useCreateAccessToken();
  const revoke = useRevokeAccessToken();
  const [issued, setIssued] = useState<string | null>(null);

  const form = useForm<z.infer<typeof tokenSchema>>({
    mode: 'uncontrolled',
    initialValues: { name: '' },
    validate: translating(tokenSchema, t, 'account'),
  });

  // Controlled separately from the form: a checkbox group is its own state and
  // putting it through the resolver would only obscure that. Starts at the
  // grant where everything has a way back.
  const [scopes, setScopes] = useState<string[]>([...DEFAULT_TOKEN_SCOPES]);

  return (
    <Card withBorder padding="md">
      <Stack gap="md">
        <Text fw={500}>{t('account.tokens')}</Text>

        <Text size="sm" c="dimmed">
          En nøgle, din AI bruger til at nå dine regnskaber gennem MCP. Den
          hører til dig, ikke til et enkelt firma — hvad den må i et firma,
          afgøres af din rolle dér, hver gang den bruges. Lav én pr. maskine, så
          du kan tilbagekalde præcis den, du mister.
        </Text>

        {issued !== null && (
          <Alert color="green" variant="light" title={t('account.tokenOnceTitle')}>
            <Stack gap="xs">
              <Text size="sm">
                {t('account.tokenOnce')}
              </Text>
              <Code block style={{ wordBreak: 'break-all' }}>
                {issued}
              </Code>
              <Group>
                <CopyButton value={issued}>
                  {({ copied, copy }) => (
                    <Button size="xs" variant="default" onClick={copy}>
                      {copied ? t('common.copied') : t('account.copyToken')}
                    </Button>
                  )}
                </CopyButton>
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => {
                    setIssued(null);
                  }}
                >
                  Skjul
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        <form
          noValidate
          onSubmit={form.onSubmit((values) => {
            create.mutate(
              { name: values.name.trim(), scopes: scopes as TokenScope[] },
              {
                onSuccess: (created) => {
                  setIssued(created.token);
                  form.reset();
                  setScopes([...DEFAULT_TOKEN_SCOPES]);
                },
              },
            );
          })}
        >
          <Group align="flex-end" gap="sm">
            <TextInput
              label={t('account.tokenName')}
              placeholder="Claude Desktop"
              description={t('account.tokenNameHelp')}
              style={{ flex: 1 }}
              key={form.key('name')}
              {...form.getInputProps('name')}
            />
            <Button type="submit" loading={create.isPending}>
              {t('account.createToken')}
            </Button>
          </Group>

          <Checkbox.Group
            mt="md"
            label={t('account.tokenScopes')}
            description={t('account.tokenScopesHelp')}
            value={scopes}
            onChange={setScopes}
          >
            <Stack gap="xs" mt="xs">
              {TOKEN_SCOPES.map((scope) => (
                <Checkbox
                  key={scope}
                  value={scope}
                  label={t(`scope.${scope}` as MessageKey)}
                  description={t(`scope.${scope}Help` as MessageKey)}
                />
              ))}
            </Stack>
          </Checkbox.Group>
        </form>

        {create.isError && (
          <ErrorMessage error={create.error} title={t('account.createTokenFailed')} />
        )}
        {revoke.isError && (
          <ErrorMessage error={revoke.error} title={t('account.revokeFailed')} />
        )}

        {tokens.isPending && (
          <Center p="md">
            <Loader size="sm" />
          </Center>
        )}

        {tokens.isError && (
          <ErrorMessage error={tokens.error} title={t('account.tokensFailed')} />
        )}

        {tokens.data !== undefined &&
          (tokens.data.items.length === 0 ? (
            <Text size="sm" c="dimmed">
              {t('account.noTokens')}
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={520}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('account.name')}</Table.Th>
                    <Table.Th>{t('account.col.status')}</Table.Th>
                    <Table.Th>{t('account.col.lastUsed')}</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {tokens.data.items.map((token) => {
                    const status = statusOf(token);
                    return (
                      <Table.Tr key={token.id}>
                        <Table.Td>{token.name}</Table.Td>
                        <Table.Td>
                          <Badge size="sm" color={status.color} variant="light">
                            {status.label}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          {token.lastUsedAt === null ? (
                            <Text size="sm" c="dimmed">
                              {t('common.never')}
                            </Text>
                          ) : (
                            formatDateTime(token.lastUsedAt)
                          )}
                        </Table.Td>
                        <Table.Td>
                          {token.revokedAt === null && (
                            <Button
                              size="xs"
                              variant="subtle"
                              color="red"
                              loading={revoke.isPending && revoke.variables === token.id}
                              onClick={() => {
                                revoke.mutate(token.id);
                              }}
                            >
                              {t('account.revoke')}
                            </Button>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ))}

        {/*
          Revoked rows stay listed rather than disappearing. A row that vanishes
          looks like a bug and hides the fact that the key existed at all -- and
          the audit log records which key made every change, so the list is what
          those entries are read against.
        */}
        <Text size="xs" c="dimmed">
          {t('account.revokedStay')}
        </Text>
      </Stack>
    </Card>
  );
}

// -----------------------------------------------------------------------------

export function AccountPage(): ReactNode {
  const t = useT();
  /*
    Email and 2FA state come from Better Auth's session, not from /v1/users/me.

    The profile endpoint does report `mfaEnabled` — and since migration 0018 it
    reports it correctly, by joining ba_users rather than reading a column
    nothing wrote. It is still read here, because this page changes that state
    and needs to re-read it immediately afterwards without waiting for a company
    token round-trip.
  */
  const session = useQuery({
    queryKey: ['session-user'],
    queryFn: getSessionUser,
  });

  return (
    <Stack gap="lg">
      <Title order={2}>{t('account.title')}</Title>

      <Text c="dimmed">
        {t('account.intro')}
      </Text>

      <ProfileCard email={session.data?.email ?? null} />
      <LanguageCard />
      {/*
        User-scoped, like everything else on this page: a push subscription is a
        browser on a device and does not change when the company switcher moves.
      */}
      <PushSettings />
      <PasswordCard />
      <TwoFactorCard
        enabled={session.data?.twoFactorEnabled === true}
        onChanged={() => {
          void session.refetch();
        }}
      />
      <AccessTokenCard />
    </Stack>
  );
}
